import { Injectable } from '@angular/core';

import { JepCookiesService } from '@jepto/shared/services/jep-cookies/jep-cookies.service';
import { JepDecodedJWTService } from '@jepto/shared/services/jep-decoded-jwt/jep-decode-jwt.service';

import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';
import { getKeyFromObject } from '@jepto/shared/utils/collection';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IAccountSubscription {
	isKPIValid: boolean;
	isClientValid: boolean;
	isBudgetsValid: boolean;
	isAnomaliesValid: boolean;
	isAutomationsValid: boolean;
	details?: {
		anomalies: {
			used: number;
			value: number;
		};
		automations: {
			used: number;
			value: number;
		};
		budgets: {
			used: number;
			value: number;
		};
		clients: {
			used: number;
			value: number;
		};
		kpi: {
			used: number;
			value: number;
		};
	};
}

const accountSubscriptionGQLQuery = `
    query accountSubscription{
      getAccountSubscription {
        isKPIValid
        isClientValid
        isBudgetsValid
        isAnomaliesValid
        isAutomationsValid
        details {
            anomalies {
                used
                value
            }
            automations {
                used
                value
            }
            budgets {
                used
                value
            } 
            clients {
                used
                value
            }
            kpi {
                used
                value
            }
        },
        status
      }
    }
`;

@Injectable({
	providedIn: 'root',
})
export class JepCurrentAccountService {
	constructor(
		private jepDecodedJWTService: JepDecodedJWTService,
		private requestService: JepRequestService,
		private jepCookieService: JepCookiesService
	) {}

	public getJWTDetails(): any {
		return this.jepDecodedJWTService.getDecodedJWT();
	}

	/**
	 * Emits the account id once the Cognito session resolves, then completes.
	 * Emits synchronously when it already has. Use instead of `getAccountId()`
	 * for anything built once at init (channels, cached request params):
	 * `getAccountId()` returns '' until the session lands.
	 */
	public accountId$(): Observable<string> {
		return this.jepDecodedJWTService
			.decodedJwtOnce$()
			.pipe(map(() => this.getAccountId()));
	}

	public getAccountId(): string {
		const jwtDetails = this.getJWTDetails();
		if (Boolean(jwtDetails))
			return jwtDetails['custom:accountId'] === null
				? this.jepCookieService.getCookieDetails('accountId')
				: jwtDetails['custom:accountId'];
		return '';
	}

	private GQLErrorChecker(gqlResponse): boolean {
		const isErrorExisting: number = getKeyFromObject(gqlResponse, 'errors');
		return isErrorExisting === -1 ? false : true;
	}

	public getCognitoUserId(): string {
		const jwtDetails: any = this.getJWTDetails();
		if (Boolean(jwtDetails)) return jwtDetails['cognito:username'];
		return '';
	}

	public getUserId(): string {
		const jwtDetails = this.getJWTDetails();
		if (Boolean(jwtDetails)) return jwtDetails['custom:userKey'];
	}

	public getJWTUserId(): string {
		const jwtDetails = this.getJWTDetails();
		if (Boolean(jwtDetails)) return jwtDetails['custom:userId'];
	}

	public getAccountSubscriptions(): Observable<any> {
		return new Observable(observer => {
			this.requestService
				.query({ query: accountSubscriptionGQLQuery, variables: {} })
				.subscribe((gqlResponse: any) => {
					const isErrorExisting: boolean =
						this.GQLErrorChecker(gqlResponse);
					if (isErrorExisting === true) {
						observer.next({
							isAnomaliesValid: null,
							isBudgetsValid: null,
							isKPIValid: null,
							isClientValid: null,
							isAutomationsValid: null,
						});
						observer.complete();
					} else {
						observer.next(gqlResponse.data.getAccountSubscription);
						observer.complete();
					}
				});
		});
	}
}
